'use client';

import { Salutation } from '@schuelerhilfe/shared/src/enums/salutation';
import { useTranslations } from 'next-intl';
import { useId, useState } from 'react';
import type { PersonalData } from '../applicationReducer';
import { FormErrorBox } from '../FormErrorBox';
import {
  validatePersonalData,
  type FieldErrors,
  type PersonalDataField,
} from '../validation';
import type { StepProps } from './stepProps';
import { StepShell } from './StepShell';
import { FormInput } from '@/components/form/FormInput';

/**
 * Step 1 — Persönliche Daten (AC 2).
 *
 * Field pairs sit side by side per the design: Vorname/Nachname,
 * Straße (wider) / Hausnr. (narrower), PLZ (narrower) / Wohnort (wider),
 * then a full-width e-mail and the two phone fields.
 *
 * A field shows its error once it has been left (blur) or once the applicant
 * has tried to advance, so messages appear where the problem is rather than
 * the CTA silently doing nothing. The message sits in a filled box *above*
 * its field (img_16), which is why the field itself renders no error text.
 */
const SALUTATIONS = [
  { value: Salutation.Mr, labelKey: 'salutationMr' },
  { value: Salutation.Mrs, labelKey: 'salutationMrs' },
  { value: Salutation.None, labelKey: 'salutationNone' },
] as const;

/** Field order and their label keys — drives the layout below. */
const FIELD_LABEL_KEYS = {
  salutation: 'salutationLegend',
  firstname: 'firstname',
  lastname: 'lastname',
  street: 'street',
  houseNumber: 'houseNumber',
  postalCode: 'postalCode',
  city: 'city',
  email: 'email',
  phone: 'phone',
  mobilePhone: 'mobilePhone',
} as const satisfies Record<PersonalDataField, string>;

export const Step1PersonalData = ({
  state,
  dispatch,
  chrome,
  onBack,
  onNext,
}: StepProps) => {
  const t = useTranslations('tutorApplication');
  const data = state.personalData;
  const idPrefix = useId();
  const [touched, setTouched] = useState<
    Partial<Record<PersonalDataField, boolean>>
  >({});
  const [showAll, setShowAll] = useState(false);

  const errors: FieldErrors<PersonalDataField> = validatePersonalData(data);
  // The rules return message keys; translate only what is actually shown.
  const visible = (field: PersonalDataField): string | undefined => {
    const key = showAll || touched[field] ? errors[field] : undefined;
    return key ? t(key) : undefined;
  };

  const set = (values: Partial<PersonalData>) =>
    dispatch({ type: 'UPDATE_PERSONAL', values });

  const markTouched = (field: PersonalDataField) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // No explicit `placeholder`: FormInput derives `${label} *` from `required`,
  // so the star comes from one place instead of being written per field.
  //
  // Rendered as a function rather than a nested component so React keeps the
  // inputs mounted across re-renders (a new component identity per keystroke
  // would remount them and drop focus).
  const renderField = (
    field: PersonalDataField,
    { required = true, type }: { required?: boolean; type?: string } = {},
  ) => {
    const error = visible(field);
    const errorId = `${idPrefix}-${field}-error`;

    return (
      <div>
        {error && (
          <div className="mb-4">
            <FormErrorBox id={errorId}>{error}</FormErrorBox>
          </div>
        )}
        <FormInput
          label={t(FIELD_LABEL_KEYS[field])}
          required={required}
          type={type}
          value={data[field]}
          error={error}
          errorId={errorId}
          // The message is the box above; the field keeps only the red border.
          showErrorText={false}
          onBlur={() => markTouched(field)}
          onChange={(event) =>
            set({ [field]: event.target.value } as Partial<PersonalData>)
          }
        />
      </div>
    );
  };

  const handleNext = () => {
    if (Object.keys(errors).length > 0) {
      // Reveal every problem at once rather than one per attempt.
      setShowAll(true);
      return;
    }
    onNext();
  };

  const salutationError = visible('salutation');
  // The summary banner tracks what is actually on screen, so it appears both
  // after a failed advance and when a single field was left empty on blur.
  const hasVisibleError = (Object.keys(errors) as PersonalDataField[]).some(
    (field) => visible(field),
  );

  return (
    <StepShell
      heading={t('step1Heading')}
      chrome={chrome}
      onBack={onBack}
      onNext={handleNext}
      hasError={hasVisibleError}
    >
      <fieldset>
        {/* The design carries no visible "Anrede" label — the row of options
            is self-explanatory and the required marker rides on the last one.
            The legend stays for screen readers. */}
        <legend className="sr-only">{t('salutationLegend')}</legend>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {SALUTATIONS.map((option, index) => (
            <label key={option.value} className="flex items-center gap-2">
              <input
                type="radio"
                name="salutation"
                value={option.value}
                checked={data.salutation === option.value}
                onChange={() => {
                  markTouched('salutation');
                  set({ salutation: option.value });
                }}
              />
              <span className="text-base">
                {t(option.labelKey)}
                {index === SALUTATIONS.length - 1 ? ' *' : ''}
              </span>
            </label>
          ))}
        </div>
        {salutationError && (
          <div className="mt-4">
            <FormErrorBox>{salutationError}</FormErrorBox>
          </div>
        )}
      </fieldset>

      <div className="grid gap-4 md:grid-cols-2">
        {renderField('firstname')}
        {renderField('lastname')}
      </div>

      <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
        {renderField('street')}
        {renderField('houseNumber')}
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_2fr]">
        {renderField('postalCode')}
        {renderField('city')}
      </div>

      {renderField('email', { type: 'email' })}

      <div className="grid gap-4 md:grid-cols-2">
        {renderField('phone', { type: 'tel' })}
        {renderField('mobilePhone', { required: false, type: 'tel' })}
      </div>
    </StepShell>
  );
};
